import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, HardDrive, Play, Loader2, Volume2, VolumeX, Pause, User, Palette } from 'lucide-react';
import { customerApi, Collectiv3Scene } from '../services/customerApi';
import { Viewer } from '@ydrive/gs-viewer';
import SceneDetailsPanel from '../components/SceneDetailsPanel';
import { useAuth } from '../contexts/AuthContext';
import * as THREE from 'three';

// Load logo from public folder
const logo = '/logo.gif';
const loadingVideoSrc = '/Kusadak.webm';


const TheCollectiv3_Admin: React.FC = () => {
  const navigate = useNavigate();
  const { authenticated } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scenes, setScenes] = useState<Collectiv3Scene[]>([]);

  // ===== 3D VIEWER STATE =====
  const [currentView, setCurrentView] = useState<'gallery' | '3d-viewer'>('gallery');
  const [selectedScene, setSelectedScene] = useState<Collectiv3Scene | undefined>(undefined);
  const [modelLoading, setModelLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const modelContainerRef = React.useRef<HTMLDivElement>(null);
  const progressTextRef = React.useRef<HTMLParagraphElement>(null);
  const viewerRef = React.useRef<any>(null);
  
  // ===== MULTI-PHASE LOADING STATE =====
  const [loadingPhase, setLoadingPhase] = useState<'download' | 'processing' | 'finalizing'>('download');
  const [overallProgress, setOverallProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState<string>('');
  const [isLargeScene, setIsLargeScene] = useState(false);

  //progressive load state variables
  const offMeshChangedRef = React.useRef<null | (() => void)>(null);
const settleTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
const lastMeshTickAtRef = React.useRef<number>(0);
const downloadDoneRef = React.useRef(false);

  const progressStallDetectorRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastProgressTimeRef = React.useRef<number>(0);

  // ===== AUDIO STATE =====
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioError, setAudioError] = useState(false);

  // ===== AUTO-ROTATION STATE =====
  const [isAutoRotating, setIsAutoRotating] = useState(false);
  const autoRotationRef = React.useRef<number | null>(null);
  const rotationAngleRef = React.useRef<number>(0);

  // ===== WALK STRAIGHT STATE =====
  const [isWalkingStraight, setIsWalkingStraight] = useState(false);
  const walkStraightRef = React.useRef<number | null>(null);

  // ===== RECORDING STATE =====
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
  const recordedChunksRef = React.useRef<Blob[]>([]);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  
  // Function to get camera position based on scene ID (with saved start points)
  const getCameraPositionForScene = (sceneId: string): [number, number, number] => {
    console.log(`[TheCollectiv3] Getting camera position for sceneId: "${sceneId}"`);
    
    // First check if user has saved a custom start point
    const sceneStartPoints = JSON.parse(localStorage.getItem('sceneStartPoints') || '{}');
    if (sceneStartPoints[sceneId]) {
      console.log(`[TheCollectiv3] Using saved start point for ${sceneId}:`, sceneStartPoints[sceneId]);
      return sceneStartPoints[sceneId];
    }
    
    // Fall back to hardcoded positions
    const lowerCaseId = sceneId.toLowerCase();
    console.log(`[TheCollectiv3] Checking against lowercase: "${lowerCaseId}"`);
    
    let position: [number, number, number];
    switch (lowerCaseId) {
      case 'maggie':
        position = [12.1, -1, -0.44]; //in front of stairs
        break;
      case 'officespace':
        position = [0.28, 0, 3.37];
        break;
      case 'kusadak':
        position = [0.8, 0, -1]; //in living room
        break;
      case 'wingwalker':
        position = [-38.32, 37.13, 14.82];
        break;
      case 'ezra':
        position = [-1.81,0,-1.19]; //entrance of the gallery
        break;
      case 'terrebonne':
        position = [-3257.44, 34, 2565.73]; //viewing position
        break;
        case 'vexcel-14':
        position = [0, 0, 0];
        console.log(`[TheCollectiv3] Starting vexcel-14 at origin [0, 0, 0]`);
        break;
      case 'vexcel-18':
        position = [0, 0, 0];
        console.log(`[TheCollectiv3] Starting vexcel-18 at origin [0, 0, 0]`);
        break;
      case 'via_boxed':
        return [18.02,1.9,6.25];
        break;
      case 'via_complete':
        position = [22.08, 2.31, -25.53];
        console.log(`[TheCollectiv3] Starting via_complete at origin [0, 0, 0]`);
        break;
      default:
        position = [0, 0, 0];
        console.log(`[TheCollectiv3] No specific position found, using default`);
    }
    
    console.log(`[TheCollectiv3] Returning camera position:`, position);
    return position;
  };

const getCameraLookAtForScene = (sceneId: string): [number, number, number] | undefined => {
  switch (sceneId.toLowerCase()) {
    case 'terrebonne':
      
      // To look 180° opposite, calculate a point in the opposite direction
      // then opposite is away from origin
      return [-6515, 34.3, 5130]; // Double the X,Z distance to look away
    case 'maggie':
      return undefined; // Use default lookAt 
    case 'ezra':
      return undefined; // Use default lookAt
    case 'via_boxed':
      return [8.09,1.25,5.34]; // Use default lookAt
    case 'via_complete':
      return [11.81, 2.02, -28.18]; // Use default lookAt
    // Add more as needed
    default:
      return undefined;
  }
};

  /* Function to get camera rotation based on scene ID
const getCameraRotationForScene = (sceneId: string): number => {
  switch (sceneId.toLowerCase()) {
    case 'terrebonne':
      return Math.PI; // 180° rotation
    case 'maggie':
      return 0; // No rotation (or change if needed)
    case 'ezra':
      return Math.PI / 2; // 90° rotation (if needed)
    default:
      return 0; // No rotation
  }
};*/



  // Auto-rotation functions
  const startAutoRotation = () => {
    if (!viewerRef.current || !selectedScene) {
      console.error('[TheCollectiv3] No active viewer or scene for auto-rotation');
      return;
    }

    try {
      const viewer = viewerRef.current;
      const camera = viewer.camera;
      
      if (!camera) {
        console.error('[TheCollectiv3] Camera not available for auto-rotation');
        return;
      }

      // Get initial camera position to orbit around
      const initialPosition = {
        x: camera.position.x,
        y: camera.position.y,
        z: camera.position.z
      };

      // Calculate radius from origin for orbiting
      const radius = Math.sqrt(initialPosition.x * initialPosition.x + initialPosition.z * initialPosition.z);
      const centerY = initialPosition.y; // Keep Y position relatively stable
      
      // Determine rotation parameters based on scene category
      const isIndoor = selectedScene.category?.toLowerCase() === 'indoor';
      
      // Adjust parameters for indoor vs outdoor scenes
      const rotationSpeed = isIndoor ? 0.0005 : 0.005; // Slower for indoor
      const verticalAmplitude = isIndoor ? 0.1 : 1; // Less vertical movement for indoor
      const verticalFrequency = isIndoor ? 0.02 : 0.3; // Even less bobbing frequency for indoor
      
      console.log(`[TheCollectiv3] Starting auto-rotation from position [${initialPosition.x.toFixed(2)}, ${initialPosition.y.toFixed(2)}, ${initialPosition.z.toFixed(2)}]`);
      console.log(`[TheCollectiv3] Scene type: ${isIndoor ? 'Indoor' : 'Outdoor'}, Rotation radius: ${radius.toFixed(2)}`);
      console.log(`[TheCollectiv3] Rotation settings - Speed: ${rotationSpeed}, Vertical amplitude: ${verticalAmplitude}, Frequency: ${verticalFrequency}`);
      
      setIsAutoRotating(true);
      rotationAngleRef.current = Math.atan2(initialPosition.z, initialPosition.x); // Start from current angle
      
      const rotate = () => {
        // Check if rotation should continue using ref instead of state
        if (!autoRotationRef.current || !viewerRef.current) {
          console.log('[TheCollectiv3] Stopping rotation - no viewer or rotation cancelled');
          return;
        }
        
        // Increment rotation angle with scene-appropriate speed
        rotationAngleRef.current += rotationSpeed;
        
        // Calculate new camera position with scene-appropriate vertical movement
        const newX = Math.cos(rotationAngleRef.current) * radius;
        const newZ = Math.sin(rotationAngleRef.current) * radius;
        const newY = centerY + Math.sin(rotationAngleRef.current * verticalFrequency) * verticalAmplitude;
        
        // Update camera position
        camera.position.set(newX, newY, newZ);
        
        // Make camera look at the center/origin with scene-appropriate target height
        const lookAtY = isIndoor ? centerY * 0.8 : centerY * 0.5; // Look slightly lower for indoor scenes
        camera.lookAt(0, lookAtY, 0);
        
        // Continue rotation
        autoRotationRef.current = requestAnimationFrame(rotate);
      };
      
      // Start the rotation animation
      autoRotationRef.current = requestAnimationFrame(rotate);
      
    } catch (error) {
      console.error('[TheCollectiv3] Error starting auto-rotation:', error);
      setIsAutoRotating(false);
    }
  };

  const stopAutoRotation = () => {
    console.log('[TheCollectiv3] Stopping auto-rotation');
    
    if (autoRotationRef.current) {
      cancelAnimationFrame(autoRotationRef.current);
      autoRotationRef.current = null;
    }
    
    setIsAutoRotating(false);
  };

  const toggleAutoRotation = () => {
    if (isAutoRotating) {
      stopAutoRotation();
    } else {
      startAutoRotation();
    }
  };

  // ===== WALK STRAIGHT FUNCTIONS =====
  const startWalkStraight = () => {
    if (!viewerRef.current || !selectedScene) {
      console.error('[TheCollectiv3] No active viewer or scene for walk straight');
      return;
    }

    try {
      const viewer = viewerRef.current as any;
      const camera = viewer.camera;
      
      if (!camera) {
        console.error('[TheCollectiv3] Camera not available for walk straight');
        return;
      }

      // Determine speed based on scene category and size
      const isIndoor = selectedScene.category?.toLowerCase() === 'indoor';
      const walkSpeed = isIndoor ? 0.025 : 0.075; // Ultra slow for cinematic recordings
      
      console.log(`[TheCollectiv3] Starting walk straight mode`);
      console.log(`[TheCollectiv3] Scene type: ${isIndoor ? 'Indoor' : 'Outdoor'}, Walk speed: ${walkSpeed}`);
      
      setIsWalkingStraight(true);
      
      const walk = () => {
        // Check if walking should continue
        if (!walkStraightRef.current || !viewerRef.current) {
          console.log('[TheCollectiv3] Stopping walk - no viewer or walk cancelled');
          return;
        }
        
        // Get forward direction from camera
        const forward = new THREE.Vector3(0, 0, -1);
        forward.applyQuaternion(camera.quaternion);
        forward.normalize();
        
        // Move camera forward
        camera.position.add(forward.multiplyScalar(walkSpeed));
        
        // Update controls if available
        if (viewer.controls?.update) {
          viewer.controls.update(0);
        }
        
        // Continue walking
        walkStraightRef.current = requestAnimationFrame(walk);
      };
      
      // Start the walk animation
      walkStraightRef.current = requestAnimationFrame(walk);
      
    } catch (error) {
      console.error('[TheCollectiv3] Error starting walk straight:', error);
      setIsWalkingStraight(false);
    }
  };

  const stopWalkStraight = () => {
    console.log('[TheCollectiv3] Stopping walk straight');
    
    if (walkStraightRef.current) {
      cancelAnimationFrame(walkStraightRef.current);
      walkStraightRef.current = null;
    }
    
    setIsWalkingStraight(false);
  };

  const toggleWalkStraight = () => {
    if (isWalkingStraight) {
      stopWalkStraight();
    } else {
      startWalkStraight();
    }
  };

  // Combined walk + recording toggle
  const toggleWalkRecording = async () => {
    if (isRecording || isWalkingStraight) {
      // Stop both walk and recording
      stopWalkRecording();
    } else {
      // Start both walk and recording
      await startWalkRecording();
    }
  };

  const startWalkRecording = async () => {
    // Start recording first
    await startRecording();
    
    // Wait a moment for recording to initialize
    setTimeout(() => {
      // Then start walking
      if (isRecording) {
        startWalkStraight();
      }
    }, 500);
  };

  const stopWalkRecording = () => {
    // Stop walking first
    stopWalkStraight();
    
    // Then stop recording after a brief delay
    setTimeout(() => {
      stopRecording();
    }, 1000);
  };

  // Combined rotation + recording toggle
  const toggleRotationRecording = async () => {
    if (isRecording || isAutoRotating) {
      // Stop both rotation and recording
      stopRotationRecording();
    } else {
      // Start both rotation and recording
      await startRotationRecording();
    }
  };

  // Recording functions
  const startRecording = async () => {
    if (!modelContainerRef.current || !viewerRef.current) {
      console.error('[Recording] No viewer container available');
      setRecordingError('3D viewer not ready for recording');
      return;
    }

    try {
      // Get the canvas element from the 3D viewer
      const canvas = modelContainerRef.current.querySelector('canvas');
      if (!canvas) {
        throw new Error('Canvas not found in viewer');
      }

      // Create a MediaStream from the canvas
      const stream = canvas.captureStream(30); // 30 FPS
      
      // Check if the stream has video tracks
      if (stream.getVideoTracks().length === 0) {
        throw new Error('No video tracks available from canvas');
      }

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9', // Use VP9 codec for better quality
        videoBitsPerSecond: 8000000 // 8 Mbps for good quality
      });

      mediaRecorderRef.current = mediaRecorder;
      recordedChunksRef.current = [];

      // Handle data available
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
          console.log(`[Recording] Data chunk received: ${event.data.size} bytes`);
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = () => {
        console.log('[Recording] Recording stopped, processing video...');
        downloadRecording();
      };

      // Handle errors
      mediaRecorder.onerror = (event) => {
        console.error('[Recording] MediaRecorder error:', event);
        setRecordingError('Recording failed');
        setIsRecording(false);
      };

      // Start recording
      mediaRecorder.start(1000); // Collect data every 1 second
      setIsRecording(true);
      setRecordingError(null);
      
      console.log('[Recording] Recording started');

    } catch (error) {
      console.error('[Recording] Failed to start recording:', error);
      setRecordingError(error instanceof Error ? error.message : 'Failed to start recording');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      console.log('[Recording] Recording stop requested');
    }
  };

  const downloadRecording = async () => {
    if (recordedChunksRef.current.length === 0) {
      console.warn('[Recording] No recorded chunks available');
      return;
    }

    try {
      // Create blob from recorded chunks
      const blob = new Blob(recordedChunksRef.current, { 
        type: 'video/webm' 
      });

      console.log(`[Recording] Created blob with size: ${blob.size} bytes`);

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with scene name and timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const sceneName = selectedScene?.sceneName || 'scene';
      link.download = `${sceneName}-rotation-${timestamp}.webm`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      URL.revokeObjectURL(url);
      recordedChunksRef.current = [];
      
      console.log('[Recording] Download initiated');

    } catch (error) {
      console.error('[Recording] Download failed:', error);
      setRecordingError('Failed to download recording');
    }
  };

  const startRotationRecording = async () => {
    // Start recording first
    await startRecording();
    
    // Wait a moment for recording to initialize
    setTimeout(() => {
      // Then start rotation
      if (isRecording) {
        startAutoRotation();
      }
    }, 500);
  };

  const stopRotationRecording = () => {
    // Stop rotation first
    stopAutoRotation();
    
    // Then stop recording after a brief delay
    setTimeout(() => {
      stopRecording();
    }, 1000);
  };

  // Function to capture current camera position
  const captureCurrentCameraPosition = () => {
    if (!viewerRef.current || !selectedScene) {
      console.error('[TheCollectiv3] No active viewer or scene');
      return;
    }

    try {
      const camera = viewerRef.current.camera;
      if (camera && camera.position) {
        const position = camera.position;
        const coordinates: [number, number, number] = [
          Math.round(position.x * 100) / 100,
          Math.round(position.y * 100) / 100,
          Math.round(position.z * 100) / 100
        ];

        // Calculate lookAt by projecting forward from camera direction
        const direction = new THREE.Vector3();
        camera.getWorldDirection(direction);
        const lookAt: [number, number, number] = [
          Math.round((position.x + direction.x * 10) * 100) / 100,
          Math.round((position.y + direction.y * 10) * 100) / 100,
          Math.round((position.z + direction.z * 10) * 100) / 100
        ];

        // Log both in copy-paste ready format
        console.log(`[TheCollectiv3] === CAMERA COORDINATES FOR "${selectedScene.sceneId}" ===`);
        console.log(`position: [${coordinates.join(', ')}]`);
        console.log(`lookAt:   [${lookAt.join(', ')}]`);

        const message = 
          `Camera coordinates for "${selectedScene.sceneName}":\n\n` +
          `Position: [${coordinates.join(', ')}]\n` +
          `LookAt:   [${lookAt.join(', ')}]\n\n` +
          `(Also logged to console in copy-paste format)\n\n` +
          `Save this position as the new starting point?`;

        if (confirm(message)) {
          const sceneStartPoints = JSON.parse(localStorage.getItem('sceneStartPoints') || '{}');
          sceneStartPoints[selectedScene.sceneId] = coordinates;
          localStorage.setItem('sceneStartPoints', JSON.stringify(sceneStartPoints));
          console.log(`[TheCollectiv3] Saved start point for ${selectedScene.sceneId}:`, coordinates);
          alert(`Start point saved! LookAt coordinates are in the console.`);
        }
      } else {
        console.error('[TheCollectiv3] Could not access camera position');
        alert('Unable to capture camera position. Make sure the 3D scene is fully loaded.');
      }
    } catch (error) {
      console.error('[TheCollectiv3] Error capturing camera position:', error);
      alert('Error capturing camera position. Check console for details.');
    }
  };

  // Fetch all scenes
  const fetchScenes = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[TheCollectiv3] Fetching all scenes...');
      const response = await customerApi.getAllCollectiv3Scenes();
      
      if (response.success) {
        setScenes(response.data);
        console.log(`[TheCollectiv3] Loaded ${response.data.length} scenes`);
        
        // Debug: Log thumbnail info for each scene
        response.data.forEach((scene, index) => {
          console.log(`[TheCollectiv3] Scene ${index + 1}: ${scene.sceneName}`);
          console.log(`  - Thumbnail: ${scene.thumbnail || 'NOT SET'}`);
          console.log(`  - Category: ${scene.category}`);
        });
      } else {
        throw new Error('Failed to fetch gallery scenes');
      }
    } catch (err) {
      console.error('[TheCollectiv3] Error fetching scenes:', err);
      setError(err instanceof Error ? err.message : 'Failed to load gallery scenes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScenes();
  }, []);

  // Get category icon
  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'art':
        return <Palette className="w-3 h-3" />;
      case 'people':
        return <User className="w-3 h-3" />;
      default:
        return <HardDrive className="w-3 h-3" />;
    }
  };

  // Get category color
  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'art':
        return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      case 'outdoor':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'indoor':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'people':
        return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      default:
        return 'bg-[#0CB4E4]/20 text-[#0CB4E4] border-[#0CB4E4]/30';
    }
  };

  // ===== 3D VIEWER LOGIC =====
  const handleSceneClick = async (scene: Collectiv3Scene) => {
    // Check file size for large scene notification
    try {
      const response = await fetch(scene.gaus, { method: 'HEAD' });
      const contentLength = response.headers.get('content-length');
      if (contentLength) {
        const fileSizeInMB = parseInt(contentLength) / (1024 * 1024);
        setIsLargeScene(fileSizeInMB > 500);
      }
    } catch (error) {
      console.warn('[TheCollectiv3] Could not check file size:', error);
      setIsLargeScene(false);
    }

    try {
    console.log(`[TheCollectiv3] Loading 3D viewer for scene: ${scene.sceneName}`);
    
    // STEP 1: Cleanup any existing viewer
    if (viewerRef.current && viewerRef.current !== 'cleaning') {
      try {
        viewerRef.current.dispose();
      } catch (error) {
        console.warn('[TheCollectiv3] Error disposing previous viewer:', error);
      }
      viewerRef.current = null;
    }
    
    // STEP 2: Set initial state
    setCurrentView('3d-viewer');
    setSelectedScene(scene);
    setModelLoading(true);
    setLoadingProgress(0);
    setLoadingPhase('download');
    setOverallProgress(0);
    setProcessingStatus('');
    
    // STEP 3: Basic DOM yield
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // STEP 4: Setup loading text
    if (progressTextRef.current) {
      const fullText = scene.loadingDescription || `Loading ${scene.sceneName}...`;
      const sentences = fullText
        .split(/(?<=[.!?])\s+/)
        .filter(sentence => sentence.trim().length > 0)
        .map(sentence => sentence.trim());
      progressTextRef.current.textContent = sentences.join('\n');
    }
    
    // STEP 5: Container check
    if (!modelContainerRef.current) {
      throw new Error('3D viewer container not found in DOM');
    }
    
    // STEP 6: Create viewer
    console.log(`[TheCollectiv3] Scene details:`, {
      sceneId: scene.sceneId,
      sceneName: scene.sceneName,
      gausFile: scene.gaus
    });

    const cameraPosition = getCameraPositionForScene(scene.sceneId);
    const cameraLookAt = getCameraLookAtForScene(scene.sceneId);
    console.log(`[TheCollectiv3] Using camera position:`, cameraPosition);
    console.log(`[TheCollectiv3] Using camera lookAt:`, cameraLookAt);

    const viewerOptions: any = {
      sharedMemoryForWorkers: false,
      rootElement: modelContainerRef.current,
      initialCameraPosition: cameraPosition,
      enableOptimizations: true,
    };

    // Only add initialCameraLookAt if we have a custom one
    if (cameraLookAt) {
      viewerOptions.initialCameraLookAt = cameraLookAt;
    }

    const viewer = new Viewer(viewerOptions);
    viewerRef.current = viewer;

// STEP 7: Load scene with progressiveLoad: false but new structure
let lastProgress = 0;
console.log('[DEBUG] Testing new approach with progressiveLoad: false');

try {
  const sceneLoadPromise = viewer.addSplatScene(scene.gaus, {
    showLoadingUI: false,
   // progressiveLoad: false, // <- Test with this disabled
    ...(scene.envMap && { 
      environmentMapPath: scene.envMap, 
      deferEnvironmentMap: true 
    }),
    ...(scene.imagePoses && { 
      imagePosesPath: scene.imagePoses, 
      deferImagePoses: true 
    }),

    

    onProgress: (percent: number) => {
      console.log('[DEBUG] Non-progressive onProgress:', percent);
      
      if (percent < lastProgress) return;
      lastProgress = percent;
      
      // Simple linear progress mapping
      const overallProgress = Math.round(percent * 0.9); // Map to 0-90%
      
      if (percent < 100) {
        setLoadingPhase('download');
        setOverallProgress(overallProgress);
        setLoadingProgress(percent);
        setProcessingStatus(`Loading: ${Math.round(percent)}%`);
      } else {
        // When we hit 100%, go to finalizing
        setLoadingPhase('finalizing');
        setProcessingStatus('Finalizing...');
        
        // Animate 90% to 100%
        let currentProgress = 90;
        const finalize = () => {
          currentProgress = Math.min(currentProgress + 5, 100);
          setOverallProgress(currentProgress);
          
          if (currentProgress < 100) {
            setTimeout(finalize, 50);
          } else {
            // Don't set modelLoading = false here, wait for Promise
          }
        };
        finalize();
      }
    },
  });

  console.log('[DEBUG] Waiting for scene load Promise...');
  await sceneLoadPromise;
  console.log('[DEBUG] Scene load Promise resolved - should be complete');

  // STEP 8: Start viewer
  console.log('[DEBUG] Starting viewer...');
  viewer.start();
  console.log('[DEBUG] Viewer started');

  /* STEP 8.5: Apply camera rotation if needed
  const rotation = getCameraRotationForScene(scene.sceneId);
  if (rotation !== 0) {
    setTimeout(() => {
      try {
        // Type assertion to access camera
        const viewerWithCamera = viewer as any;
        if (viewerWithCamera.camera) {
          viewerWithCamera.camera.rotation.y += rotation;
          
          // Update controls if they exist
          if (viewerWithCamera.controls?.update) {
            viewerWithCamera.controls.update();
          }
          
          console.log(`🔄 Camera rotated ${(rotation * 180 / Math.PI)}° for ${scene.sceneId}`);
        }
      } catch (error) {
        console.error('[DEBUG] Error rotating camera:', error);
      }
    }, 200);
  }*/

  // STEP 9: Complete loading
  setModelLoading(false);
  console.log('[DEBUG] Loading marked complete - scene should be visible');
  
 

  // Setup audio
  if (scene.audio) {
    setTimeout(() => {
      setupAudio(scene);
    }, 500);
  }

} catch (error) {
  console.error('[DEBUG] Error in scene loading:', error);
  setModelLoading(false);
  setSelectedScene(undefined);
  setCurrentView('gallery');
  throw error;
}
    
    console.log(`[TheCollectiv3] Scene loaded: ${scene.sceneName}`);
    
  } catch (error) {
    console.error('[TheCollectiv3] Loading error:', error);
    setModelLoading(false);
    setSelectedScene(undefined);
    setCurrentView('gallery');
    
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    alert(`Failed to load 3D viewer: ${errorMessage}`);
  }
};



// Add debugging useEffect
useEffect(() => {
  console.log(`[DEBUG] State - Progress: ${overallProgress}%, Phase: ${loadingPhase}, Loading: ${modelLoading}`);
}, [overallProgress, loadingPhase, modelLoading]);

  // Separate audio setup function
  const setupAudio = (scene: Collectiv3Scene) => {
    // Early return if no audio file exists for this scene
    if (!scene.audio) {
      console.log('[TheCollectiv3] No audio file specified for this scene');
      return;
    }
    
    if (!audioRef.current) {
      console.log('[TheCollectiv3] Audio ref not available');
      return;
    }

    try {
      console.log('[TheCollectiv3] Setting up audio separately:', scene.audio);
      
      // Reset audio state
      setAudioError(false);
      setIsAudioPlaying(false);
      
      const audio = audioRef.current;
      const fullAudioUrl = `${window.location.origin}${scene.audio}`;
      
      console.log('[TheCollectiv3] Audio URL:', fullAudioUrl);
      console.log('[TheCollectiv3] Audio element:', audio);
      console.log('[TheCollectiv3] Audio readyState before:', audio.readyState);
      
      // Clean up any existing listeners
      audio.removeEventListener('canplay', autoPlayHandler);
      audio.removeEventListener('error', errorHandler);
      audio.removeEventListener('loadeddata', loadedDataHandler);
      
      // Reset and set source
      audio.pause();
      audio.currentTime = 0;
      audio.src = fullAudioUrl;
      
      console.log('[TheCollectiv3] Audio src set to:', audio.src);
      
      // Define handlers
      function autoPlayHandler() {
        console.log('[TheCollectiv3] Audio canplay event fired');
        console.log('[TheCollectiv3] Audio readyState:', audio.readyState);
        
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsAudioPlaying(true);
              setAudioError(false);
              console.log('[TheCollectiv3] Auto-play started successfully');
            })
            .catch(error => {
              console.warn('[TheCollectiv3] Auto-play failed:', error);
              setIsAudioPlaying(false);
            });
        }
        
        audio.removeEventListener('canplay', autoPlayHandler);
      }
      
      function errorHandler(e: any) {
        console.error('[TheCollectiv3] Audio error:', e);
        console.error('[TheCollectiv3] Audio error details:', audio.error);
        setAudioError(true);
        setIsAudioPlaying(false);
        
        audio.removeEventListener('error', errorHandler);
        audio.removeEventListener('canplay', autoPlayHandler);
      }
      
      function loadedDataHandler() {
        console.log('[TheCollectiv3] Audio loadeddata event fired');
        console.log('[TheCollectiv3] Audio readyState after loadeddata:', audio.readyState);
      }
      
      // Add event listeners
      audio.addEventListener('canplay', autoPlayHandler);
      audio.addEventListener('error', errorHandler);
      audio.addEventListener('loadeddata', loadedDataHandler);
      
      // Force load
      audio.load();
      
      console.log('[TheCollectiv3] Audio load() called');
      
      // Debug: Check if the URL is accessible
      fetch(fullAudioUrl, { method: 'HEAD' })
        .then(response => {
          console.log('[TheCollectiv3] Audio URL accessibility check:', response.status, response.statusText);
        })
        .catch(fetchError => {
          console.error('[TheCollectiv3] Audio URL not accessible:', fetchError);
        });
        
    } catch (error) {
      console.error('[TheCollectiv3] Audio setup error:', error);
      setAudioError(true);
    }
  };

  // Close viewer function
  const close3DViewer = () => {
  console.log(`[TheCollectiv3] Closing 3D viewer`);
  
  // Clean up progressive loading refs and timers
  if (offMeshChangedRef.current) {
    try { 
      offMeshChangedRef.current(); 
    } catch {}
    offMeshChangedRef.current = null;
  }
  if (settleTimerRef.current) {
    clearTimeout(settleTimerRef.current);
    settleTimerRef.current = null;
  }
  downloadDoneRef.current = false;
  lastMeshTickAtRef.current = 0;
  
  // Clean up any other running intervals
  if ((window as any).progressInterval) {
    clearInterval((window as any).progressInterval);
    (window as any).progressInterval = null;
  }
  
  // Stop and cleanup audio
  if (audioRef.current) {
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsAudioPlaying(false);
    setAudioError(false);
  }
  
  // Stop auto-rotation
  if (autoRotationRef.current) {
    cancelAnimationFrame(autoRotationRef.current);
    autoRotationRef.current = null;
  }
  setIsAutoRotating(false);
  
  // Stop walk straight
  if (walkStraightRef.current) {
    cancelAnimationFrame(walkStraightRef.current);
    walkStraightRef.current = null;
  }
  setIsWalkingStraight(false);
  
  // Dispose viewer properly
  if (viewerRef.current && viewerRef.current !== 'cleaning') {
    try {
      const viewer = viewerRef.current;
      viewerRef.current = 'cleaning' as any;
      
      if (typeof viewer.dispose === 'function') {
        viewer.dispose();
      }
      console.log('Viewer disposed successfully');
    } catch (error) {
      console.warn('[TheCollectiv3] Error disposing viewer:', error);
    }
    viewerRef.current = null;
  }
  
  // Reset all viewer state
  setSelectedScene(undefined);
  setModelLoading(false);
  setCurrentView('gallery');
  setLoadingPhase('download');
  setOverallProgress(0);
  setProcessingStatus('');
  setIsLargeScene(false);
  
  // Clear any loading text
  if (progressTextRef.current) {
    progressTextRef.current.textContent = '';
  }
  
  console.log('[TheCollectiv3] Progressive loading cleanup complete');
};

// Also update your component cleanup useEffect:
useEffect(() => {
  return () => {
    // Cleanup progressive loading
    if (offMeshChangedRef.current) {
      try { 
        offMeshChangedRef.current(); 
      } catch {}
      offMeshChangedRef.current = null;
    }
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    downloadDoneRef.current = false;
    
    // Cleanup audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    
    // Cleanup 3D viewer
    if (viewerRef.current && viewerRef.current !== 'cleaning') {
      try {
        if (typeof viewerRef.current.dispose === 'function') {
          viewerRef.current.dispose();
        }
      } catch (error) {
        console.warn('Unmount cleanup error (ignoring):', error);
      } finally {
        viewerRef.current = null;
      }
    }
  };
}, []);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && currentView === '3d-viewer') {
        event.preventDefault();
        event.stopPropagation();
        close3DViewer();
      }
    };

    if (currentView === '3d-viewer') {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [currentView]);

  // Component cleanup
  useEffect(() => {
    return () => {
      // Stop auto-rotation
      if (autoRotationRef.current) {
        cancelAnimationFrame(autoRotationRef.current);
      }
      
      // Stop walk straight
      if (walkStraightRef.current) {
        cancelAnimationFrame(walkStraightRef.current);
      }
      
      // Cleanup audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      
      // Cleanup 3D viewer
      if (viewerRef.current && viewerRef.current !== 'cleaning') {
        try {
          if (typeof viewerRef.current.dispose === 'function') {
            viewerRef.current.dispose();
          }
        } catch (error) {
          console.warn('Unmount cleanup error (ignoring):', error);
        } finally {
          viewerRef.current = null;
        }
      }
    };
  }, []);

  // ===== AUDIO CONTROL FUNCTIONS =====
  const toggleAudio = () => {
    if (!audioRef.current) {
      console.error('[TheCollectiv3] Audio ref not available');
      return;
    }

    console.log('[TheCollectiv3] Toggle audio, currently playing:', isAudioPlaying);
    
    if (isAudioPlaying) {
      audioRef.current.pause();
      setIsAudioPlaying(false);
      console.log('[TheCollectiv3] Audio paused');
    } else {
      console.log('[TheCollectiv3] Attempting to play audio:', audioRef.current.src);
      
      if (audioRef.current.readyState < 2) {
        console.warn('[TheCollectiv3] Audio not ready to play. ReadyState:', audioRef.current.readyState);
        alert('Audio is still loading. Please wait and try again.');
        return;
      }
      
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsAudioPlaying(true);
            setAudioError(false);
            console.log('[TheCollectiv3] Audio started playing successfully');
          })
          .catch(error => {
            console.error('[TheCollectiv3] Audio play error:', error);
            setAudioError(true);
            setIsAudioPlaying(false);
            
            if (error.name === 'NotSupportedError') {
              alert('Audio format not supported by your browser');
            } else if (error.name === 'NotAllowedError') {
              alert('Audio playback blocked by browser. Please check your audio settings.');
            } else {
              alert(`Audio error: ${error.message}`);
            }
          });
      }
    }
  };

  // ===== RENDER PUBLIC HEADER (for non-authenticated users) =====
  const renderPublicHeader = () => (
    <header className="sticky top-0 z-50 w-full bg-[#0E0D0C]/95 backdrop-blur-lg border-b border-[#0CB4E4]/20 py-3 px-3 sm:px-6 sm:py-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-1.5 sm:gap-3">
          <img src={logo} alt="CR3ATE.AI" className="h-6 sm:h-10" />
          <h1 className="font-light text-base sm:text-2xl text-[#0CB4E4] font-brand">CR3ATE.AI</h1>
        </Link>
        <div className="flex items-center gap-1.5 sm:gap-4">
          <Link to="/login" className="px-2.5 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-base text-gray-300 hover:text-white transition-colors">Sign In</Link>
          <Link to="/request-waitlist" className="px-3 py-1.5 sm:px-5 sm:py-2.5 text-xs sm:text-base bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/80 text-[#161513] rounded-lg sm:rounded-xl hover:shadow-lg transition-all font-medium">Request a Demo</Link>
        </div>
      </div>
    </header>
  );

  // ===== RENDER GALLERY VIEW =====
  const renderGalleryView = () => (
    <div className="min-h-screen bg-[#161513]">
      {/* Conditional Header - Show public header if not authenticated, Layout will handle if authenticated */}
      {!authenticated && renderPublicHeader()}
      {/* Header */}
      <div className="relative bg-[#161513] border-b border-[#0CB4E4]/20">
        {/* Logo Background */}
        <div 
          className="absolute top-0 left-0 right-0 h-full bg-cover bg-center bg-no-repeat opacity-10 z-0 pointer-events-none"
          style={{ backgroundImage: `url(${logo})` }}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 relative z-10">
          {authenticated && (
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={() => navigate(-1)} 
              className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
          </div>
          )}

          <div className="text-center">
            <h1 className="text-4xl sm:text-6xl font-bold text-white mb-4">
              <span className="text-[#0CB4E4]">The admin playground</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Experience the future of 3D reconstruction through our curated showcase of 
              photorealistic scenes captured with CR3ATE.AI technology
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {/* Loading State */}
        {loading && (
          <div className="text-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#0CB4E4] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Loading Gallery...</h3>
            <p className="text-gray-300">Preparing our 3D collection for you</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-900/20 text-red-400 p-6 rounded-xl border border-red-500/30 mb-8">
            <h3 className="font-medium mb-2">Unable to Load Gallery</h3>
            <p>{error}</p>
            <button 
              onClick={fetchScenes}
              className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* All Scenes Grid */}
        {!loading && !error && scenes.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {scenes.map((scene) => (
              <div
                key={`${scene.sceneName}-${scene.category}`}
                onClick={() => handleSceneClick(scene)}
                className="relative overflow-hidden h-[480px] bg-[#161513] shadow-lg border border-[#0CB4E4]/30 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] rounded-xl group cursor-pointer"
              >
                {/* Scene Preview */}
                <div className="absolute inset-0 w-full h-full bg-[#161513] overflow-hidden rounded-xl">
                  <div className="h-1/2 bg-gradient-to-br from-[#0CB4E4]/20 via-[#0CB4E4]/10 to-[#0CB4E4]/20 relative flex items-center justify-center">
                    {/* Thumbnail or Logo */}
                    {scene.thumbnail ? (
                      <img 
                        src={scene.thumbnail} 
                        alt={scene.sceneName}
                        className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                        onError={(e) => {
                          // Fallback to logo if thumbnail fails to load
                          console.error(`[TheCollectiv3] Thumbnail failed to load: ${scene.thumbnail} for scene: ${scene.sceneName}`);
                          (e.target as HTMLImageElement).src = logo;
                          (e.target as HTMLImageElement).className = "h-24 object-contain transition-transform duration-500 ease-out group-hover:scale-110 opacity-60";
                        }}
                        onLoad={() => {
                          console.log(`[TheCollectiv3] Thumbnail loaded successfully: ${scene.thumbnail} for scene: ${scene.sceneName}`);
                        }}
                      />
                    ) : (
                      <>
                        {console.log(`[TheCollectiv3] No thumbnail found for scene: ${scene.sceneName}, using logo fallback`)}
                        <img 
                          src={logo} 
                          alt="3D Scene Preview" 
                          className="h-24 object-contain transition-transform duration-500 ease-out group-hover:scale-110 opacity-60" 
                        />
                      </>
                    )}
                    
                    {/* Play overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="w-16 h-16 bg-[#0CB4E4] rounded-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-[#161513] ml-1" />
                      </div>
                    </div>

                    {/* Category badge */}
                    <div className={`absolute top-3 right-3 backdrop-blur-sm px-3 py-1 text-xs font-medium border rounded-full flex items-center gap-1 ${getCategoryColor(scene.category)}`}>
                      {getCategoryIcon(scene.category)}
                      <span>{scene.category}</span>
                    </div>
                  </div>
                  
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#161513] via-transparent to-transparent" />
                </div>

                {/* Scene Info - Enhanced with cardDescription and credits */}
                <div className="absolute bottom-0 left-0 right-0 p-5 z-10 bg-gradient-to-t from-[#161513] via-[#161513]/95 to-transparent min-h-[240px] flex flex-col justify-end">
                  <h3 className="font-semibold text-white text-base mb-2 line-clamp-1" title={scene.sceneName}>
                    {scene.sceneName}
                  </h3>
                  
                  {/* Card Description */}
                  <p className="text-sm text-gray-300 line-clamp-3 mb-3" title={scene.cardDescription}>
                    {scene.cardDescription}
                  </p>

                  {/* Credits - Show artist tag if category is Art */}
                  {scene.credits && (
                    <div className="mb-3">
                      <span className="text-xs text-gray-400">
                        {scene.category.toLowerCase() === 'art' ? 'Artist: ' : 'Credits: '}
                      </span>
                      <span className="text-xs text-[#0CB4E4] font-medium">
                        {scene.credits}
                      </span>
                    </div>
                  )}

                  {/* Location */}
                  {scene.location && (
                    <div className="mb-3">
                      <span className="text-xs text-gray-400">Location: </span>
                      <span className="text-xs text-gray-300 font-medium">
                        {scene.location}
                      </span>
                    </div>
                  )}

                  {/* Scene Stats */}
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <div className="flex items-center gap-1">
                      <HardDrive className="w-3 h-3" />
                      <span>3D Scene</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Play className="w-3 h-3" />
                      <span>Interactive</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && scenes.length === 0 && (
          <div className="text-center py-20">
            <img 
              src={logo} 
              alt="No scenes" 
              className="w-16 h-16 mx-auto mb-4 object-contain opacity-50" 
            />
            <h3 className="text-lg font-medium text-white mb-2">No Scenes Available</h3>
            <p className="text-gray-300">
              The gallery collection is being prepared. Check back soon!
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // ===== RENDER MULTI-PHASE LOADING UI =====
  const renderLoadingUI = () => (
  <div className="fixed inset-0 z-[1002]">
    {/* Background video */}
    <video
      key="loading-bg-video"
      src={loadingVideoSrc}
      className="absolute inset-0 w-full h-full object-cover"
      autoPlay
      muted
      loop
      playsInline
    />

    {/* Subtle vignette for readability */}
    <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/0 to-black/20 pointer-events-none" />

    {/* Positioned, elegant glass panel */}
    <div className="absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 w-[32rem] max-w-[92%]">
      {/* Gradient border wrapper */}
      <div className="p-[1.5px] rounded-3xl bg-gradient-to-br from-white/25 via-[#0CB4E4]/40 to-transparent shadow-2xl">
        <aside className="relative rounded-3xl bg-[#0E0D0C]/[0.15] backdrop-blur-xl border border-white/10 overflow-hidden">
          {/* Close Button */}
          <button
            onClick={close3DViewer}
            className="absolute top-4 right-4 z-10 w-8 h-8 grid place-items-center bg-white/10 hover:bg-white/20 rounded-full text-white/60 hover:text-white transition-colors"
            aria-label="Close viewer"
          >
            <span className="text-xl font-light">✕</span>
          </button>
          
          {/* Header */}
          <div className="px-6 pt-5 pb-3 border-b border-white/10">
            <h2 className="text-white text-xl font-semibold tracking-tight truncate pr-8">
              {selectedScene?.sceneName || 'Loading Scene'}
            </h2>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-5">
            {/* Description (scrollable if long) */}
            <div className="max-h-40 overflow-y-auto pr-1">
              <p
                ref={progressTextRef}
                className="text-white/90 text-[0.95rem] leading-6 whitespace-pre-wrap break-words"
                style={{
                  fontFamily: "'Source Sans Pro', 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                {selectedScene?.loadingDescription}
              </p>
            </div>

            {/* Progress */}
            <div className="space-y-3">
              {/* Bar with inline % */}
              <div className="relative w-full h-3.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/85 transition-all duration-300 ease-out"
                  style={{ width: `${overallProgress}%` }}
                />
                <div className="absolute inset-0 grid place-items-center">
                  <span className="text-xs font-medium text-white/90 drop-shadow">
                    {overallProgress}% Complete
                  </span>
                </div>
              </div>
              {/* Large scene warning message */}
              {isLargeScene && (
                <div className="text-center">
                  <p className="text-xs text-[#0CB4E4] bg-[#0CB4E4]/10 px-3 py-2 rounded-lg border border-[#0CB4E4]/30">
                    This is a large scene and may take upto 3 minutes to Load
                   </p>
                </div>
              )} 
              {/* Phase chips */}
              <div className="flex items-center justify-between text-[0.85rem]">
                <PhaseChip
                  active={loadingPhase === 'download'}
                  done={overallProgress > 33}
                  label="Download"
                />
                <PhaseChip
                  active={loadingPhase === 'processing'}
                  done={overallProgress > 80}
                  label="Processing"
                />
                <PhaseChip
                  active={loadingPhase === 'finalizing'}
                  done={overallProgress === 100}
                  label="Finalizing"
                />
              </div>
            </div>
          </div>

          {/* Footer tagline */}
          <div className="px-6 py-4 border-t border-white/10 text-center">
            <p className="text-white/95 text-sm tracking-wide">
              Experience it like you are there!
            </p>
          </div>
        </aside>
      </div>
    </div>
  </div>
);

  // PhaseChip Component
  const PhaseChip: React.FC<{ active?: boolean; done?: boolean; label: string }> = ({
    active,
    done,
    label
  }) => {
    const base =
      "inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[0.82rem]";
    if (done) {
      return (
        <span className={`${base} border-white/15 text-white/70 bg-white/5`}>
          <svg className="w-4 h-4 text-white/60" viewBox="0 0 24 24" fill="none">
            <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {label}
        </span>
      );
    }
    if (active) {
      return (
        <span className={`${base} border-[#0CB4E4]/40 text-[#0CB4E4] bg-[#0CB4E4]/10`}>
          <span className="w-2 h-2 rounded-full bg-[#0CB4E4] animate-pulse" />
          {label}
        </span>
      );
    }
    return (
      <span className={`${base} border-white/10 text-white/50 bg-white/5`}>
        <span className="w-2 h-2 rounded-full bg-white/30" />
        {label}
      </span>
    );
  };

  // ===== RENDER 3D VIEWER =====
  const render3DViewer = () => (
    <div className="fixed inset-0 w-full h-full bg-black z-[999]">
      {/* Close button */}
      <div className="fixed top-4 right-4 z-[1001]">
        <button
          onClick={close3DViewer}
          className="bg-[#161513]/90 backdrop-blur-sm text-white px-6 py-3 shadow-lg hover:bg-[#161513] transition-all duration-200 flex items-center gap-3 font-medium border border-[#0CB4E4]/30 rounded-xl"
        >
          <span className="text-lg">✕</span>
          <span>Close</span>
          <span className="text-xs text-gray-400">(ESC)</span>
        </button>
      </div>

      {/* Brand overlay */}
      <div className="fixed top-4 left-4 z-[1001] flex items-center gap-2">
        <img src={logo} alt="CR3ATE.AI" className="h-10 w-10 object-contain" />
        <h1 className="font-light text-xl text-[#0CB4E4] font-brand">
          CR3ATE.AI
        </h1>
      </div>

      {/* Multi-phase Loading UI */}
      {modelLoading && renderLoadingUI()}

      {/* 3D Model Viewer Container */}
      <div
        ref={modelContainerRef}
        className="absolute inset-0 w-full h-full"
        style={{
          zIndex: 1000,
          backgroundColor: "black"
        }}
      />

      {/* Scene Details Panel - Shows sceneDescription when loaded */}
      {selectedScene && !modelLoading && (  
        <SceneDetailsPanel 
          model={{
            fileName: selectedScene.sceneName,
            description: selectedScene.sceneDescription,
            fileType: 'gaus',
            category: selectedScene.category,
            credits: selectedScene.credits,
            location: selectedScene.location,
            KnowMore: selectedScene.KnowMore,
            createdAt: selectedScene.createdAt,
          }}
          onClose={close3DViewer}
        />
      )}

      {/* Controls Row */}
      {!modelLoading && selectedScene && (
        <div className="fixed bottom-4 left-4 z-[1001] flex flex-col gap-3">
          {/* Audio Controls */}
          {selectedScene.audio && (
            <>
              {/* Hidden Audio Element */}
              <audio
                ref={audioRef}
                loop={true}
                onEnded={() => {
                  setIsAudioPlaying(false);
                  console.log('[TheCollectiv3] Audio ended');
                }}
                onError={(e) => {
                  console.error('[TheCollectiv3] Audio error event:', e);
                  setAudioError(true);
                  setIsAudioPlaying(false);
                }}
                onCanPlay={() => {
                  console.log('[TheCollectiv3] Audio can play - ready to use!');
                  setAudioError(false);
                }}
                preload="auto"
              />
              
              {/* Audio Control Button */}
              <button
                onClick={toggleAudio}
                className="bg-[#161513]/90 backdrop-blur-sm text-white p-4 shadow-lg hover:bg-[#161513] transition-all duration-200 rounded-xl border border-[#0CB4E4]/30 flex items-center gap-3"
                title={isAudioPlaying ? 'Pause artist narration' : 'Play artist narration'}
              >
                {audioError ? (
                  <VolumeX className="w-6 h-6 text-red-400" />
                ) : isAudioPlaying ? (
                  <Pause className="w-6 h-6 text-[#0CB4E4]" />
                ) : (
                  <Volume2 className="w-6 h-6 text-[#0CB4E4]" />
                )}
                <div className="text-sm">
                  <div className="font-medium">Artist Narration</div>
                  <div className="text-xs text-gray-400">
                    {audioError ? 'Audio Error' : isAudioPlaying ? 'Playing' : 'Click to play'}
                  </div>
                </div>
              </button>
            </>
          )}

          {/* Auto-Rotation Control Button */}
          <button
            onClick={toggleAutoRotation}
            className="bg-[#161513]/90 backdrop-blur-sm text-white p-4 shadow-lg hover:bg-[#161513] transition-all duration-200 rounded-xl border border-[#0CB4E4]/30 flex items-center gap-3"
            title={isAutoRotating ? 'Stop auto rotation' : 'Start auto rotation'}
          >
            <div className={`w-6 h-6 flex items-center justify-center transition-transform duration-300 ${isAutoRotating ? 'animate-spin text-[#0CB4E4]' : 'text-[#0CB4E4]'}`}>
              <span className="text-lg">🔄</span>
            </div>
            <div className="text-sm">
              <div className="font-medium">Auto Rotate</div>
              <div className="text-xs text-gray-400">
                {isAutoRotating ? 'Rotating...' : 'Click to rotate'}
              </div>
            </div>
          </button>

          {/* Record Rotation Button */}
          <button
            onClick={toggleRotationRecording}
            className={`backdrop-blur-sm text-white p-4 shadow-lg transition-all duration-200 rounded-xl border flex items-center gap-3 ${
              isRecording || isAutoRotating 
                ? 'bg-red-600/90 hover:bg-red-600 border-red-500/30' 
                : 'bg-[#161513]/90 hover:bg-[#161513] border-[#0CB4E4]/30'
            }`}
            title={isRecording ? 'Stop recording rotation' : 'Record rotation as video'}
            disabled={!!recordingError}
          >
            <div className="w-6 h-6 flex items-center justify-center text-[#0CB4E4]">
              {isRecording ? (
                <span className="text-lg animate-pulse">⏹️</span>
              ) : (
                <span className="text-lg">🎥</span>
              )}
            </div>
            <div className="text-sm">
              <div className="font-medium">
                {isRecording ? 'Stop Recording' : 'Record Rotation'}
              </div>
              <div className="text-xs text-gray-400">
                {recordingError ? 'Error' : isRecording ? 'Recording...' : 'Click to record'}
              </div>
            </div>
          </button>

          {/* Walk Straight Control Button */}
          <button
            onClick={toggleWalkStraight}
            className="bg-[#161513]/90 backdrop-blur-sm text-white p-4 shadow-lg hover:bg-[#161513] transition-all duration-200 rounded-xl border border-[#0CB4E4]/30 flex items-center gap-3"
            title={isWalkingStraight ? 'Stop walking' : 'Start walking straight'}
          >
            <div className={`w-6 h-6 flex items-center justify-center text-[#0CB4E4] ${isWalkingStraight ? 'animate-pulse' : ''}`}>
              <span className="text-lg">🚶</span>
            </div>
            <div className="text-sm">
              <div className="font-medium">Walk Straight</div>
              <div className="text-xs text-gray-400">
                {isWalkingStraight ? 'Walking...' : 'Click to walk'}
              </div>
            </div>
          </button>

          {/* Record Walk Button */}
          <button
            onClick={toggleWalkRecording}
            className={`backdrop-blur-sm text-white p-4 shadow-lg transition-all duration-200 rounded-xl border flex items-center gap-3 ${
              isRecording || isWalkingStraight 
                ? 'bg-red-600/90 hover:bg-red-600 border-red-500/30' 
                : 'bg-[#161513]/90 hover:bg-[#161513] border-[#0CB4E4]/30'
            }`}
            title={isRecording ? 'Stop recording walk' : 'Record walk as video'}
            disabled={!!recordingError}
          >
            <div className="w-6 h-6 flex items-center justify-center text-[#0CB4E4]">
              {isRecording && isWalkingStraight ? (
                <span className="text-lg animate-pulse">⏹️</span>
              ) : (
                <span className="text-lg">🎬</span>
              )}
            </div>
            <div className="text-sm">
              <div className="font-medium">
                {isRecording && isWalkingStraight ? 'Stop Recording' : 'Record Walk'}
              </div>
              <div className="text-xs text-gray-400">
                {recordingError ? 'Error' : (isRecording && isWalkingStraight) ? 'Recording...' : 'Click to record'}
              </div>
            </div>
          </button>

          {/* Recording Error Display */}
          {recordingError && (
            <div className="bg-red-900/20 text-red-400 p-3 rounded-xl border border-red-500/30 text-sm">
              <div className="font-medium mb-1">Recording Error</div>
              <div className="text-xs">{recordingError}</div>
              <button
                onClick={() => setRecordingError(null)}
                className="mt-2 text-xs underline hover:no-underline"
              >
                Dismiss
              </button>
            </div>
          )}

        </div>
      )}

      {/* Set Start Point Button */}
      {!modelLoading && selectedScene && (
        <div className="fixed bottom-4 right-4 z-[1001] flex flex-col gap-3">
          <button
            onClick={captureCurrentCameraPosition}
            className="bg-[#161513]/90 backdrop-blur-sm text-white p-4 shadow-lg hover:bg-[#161513] transition-all duration-200 rounded-xl border border-[#0CB4E4]/30 flex items-center gap-3"
            title="Save current camera position as start point"
          >
            <div className="w-6 h-6 text-[#0CB4E4] flex items-center justify-center">
              <span className="text-lg">📍</span>
            </div>
            <div className="text-sm">
              <div className="font-medium">Set Start Point</div>
              <div className="text-xs text-gray-400">Save camera position</div>
            </div>
          </button>
        </div>
      )}
    </div>
  );

  // ===== MAIN RENDER =====
  return (
    <>
      {currentView === 'gallery' && renderGalleryView()}
      {currentView === '3d-viewer' && render3DViewer()}
    </>
  );
};

export default TheCollectiv3_Admin;